/**
 * «Ricevi notifiche», sull'utente collegato. **Uno solo, in due vestiti.**
 *
 * Nato dentro `MenuUtente`, è uscito di lì quando la pagina del profilo ha
 * avuto bisogno dello stesso comando: la parte delicata non è il disegno, è
 * cosa succede quando il salvataggio fallisce, e quella non va scritta due
 * volte. Due copie divergono alla prima correzione fatta su una sola.
 *
 * Ciò che cambia fra le due varianti è solo l'involucro:
 *
 *   `menu`   → una voce di menu (`menuitemcheckbox`), perché lì dentro sta in
 *              mezzo ad altre voci e le frecce devono attraversarla.
 *   `pagina` → un interruttore a sé (`switch`), con accanto la frase che
 *              spiega cosa si accende: nel menu non ci starebbe, in una pagina
 *              un comando senza spiegazione è un comando che non si tocca.
 *
 * L'interruttore si muove **subito**, prima della risposta del backend, perché
 * mezzo secondo di attesa su un comando così piccolo si legge come un guasto.
 * Ma se la chiamata fallisce **torna indietro** e lo scrive: un interruttore
 * rimasto acceso mentre il backend non ha registrato niente farebbe contare su
 * avvisi che non arriveranno mai, e ce se ne accorgerebbe alla prima scadenza
 * mancata.
 */
import { useEffect, useRef, useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { autenticazione } from '../api/scadenziario.ts';
import type { Utente } from '../api/tipi-dati.ts';
import { IconaCampana } from './Icona.tsx';

export function InterruttoreNotifiche({
  utente, ricarica, variante = 'menu',
}: {
  utente: Utente;
  ricarica: () => Promise<void>;
  variante?: 'menu' | 'pagina';
}) {
  const [riceve, setRiceve] = useState(utente.riceveNotifiche);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /*
   * Il doppio clic si ferma qui e non con `disabled`.
   *
   * Disabilitare il bottone mentre la chiamata è in volo gli farebbe perdere
   * il focus — il browser lo sposta sul `<body>` — e da lì Esc non arriva più
   * al menu: chi lo usa da tastiera resterebbe chiuso dentro finché il
   * backend non risponde. Il ref si aggiorna subito, senza aspettare un
   * ridisegno, quindi due clic ravvicinati non partono in coppia.
   */
  const inVolo = useRef(false);

  // Il contesto resta la verità: se la preferenza cambia altrove — dalla
  // pagina Utenti, o alla prossima rivalidazione automatica — l'interruttore
  // si adegua invece di mostrare la copia congelata al primo disegno.
  useEffect(() => { setRiceve(utente.riceveNotifiche); }, [utente.riceveNotifiche]);

  async function cambia() {
    if (inVolo.current) return;
    inVolo.current = true;

    const nuovo = !riceve;
    setRiceve(nuovo);
    setErrore(null);
    setInCorso(true);

    try {
      await autenticazione.cambiaPreferenze({ riceveNotifiche: nuovo });
      await ricarica();
    } catch (e) {
      setRiceve(!nuovo);
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    } finally {
      inVolo.current = false;
      setInCorso(false);
    }
  }

  /*
   * Un id per variante, e non uno solo.
   *
   * Sulla pagina del profilo l'interruttore c'è due volte insieme — quello
   * della pagina e quello dentro al menu del bollino, se lo si apre — e due
   * elementi con lo stesso `id` fanno puntare entrambi gli `aria-describedby`
   * al primo che il browser incontra: il messaggio d'errore di uno finirebbe
   * letto come se fosse dell'altro.
   */
  const idErrore = `interruttore-notifiche-errore-${variante}`;

  const messaggio = errore === null ? null : (
    <p className="menu-errore" id={idErrore} role="alert">
      «Ricevi notifiche» non è stato salvato: {errore} — l'interruttore è tornato com'era.
    </p>
  );

  if (variante === 'pagina') {
    return (
      <>
        <button
          type="button"
          role="switch"
          aria-checked={riceve}
          aria-describedby={errore === null ? undefined : idErrore}
          aria-busy={inCorso}
          className="menu-voce interruttore-riquadro"
          onClick={() => void cambia()}
        >
          <IconaCampana />
          <span className="menu-corpo">
            <span className="menu-testo">Ricevi gli avvisi di scadenza via email</span>
            <span className="menu-nota">
              {riceve
                ? `Arrivano a ${utente.email}, di prima mattina, e solo nei giorni in cui c'è qualcosa da segnalare.`
                : 'Spento: non riceverai nessun avviso, nemmeno per le scadenze già passate. Le scadenze restano comunque nell\'elenco.'}
            </span>
          </span>
          <span className="menu-leva" aria-hidden="true" />
        </button>
        {messaggio}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={riceve}
        aria-describedby={errore === null ? undefined : idErrore}
        aria-busy={inCorso}
        className="menu-voce"
        onClick={() => void cambia()}
      >
        <IconaCampana />
        <span className="menu-testo">Ricevi notifiche</span>
        <span className="menu-leva" aria-hidden="true" />
      </button>
      {messaggio}
    </>
  );
}

